import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText, Plus, Search, Filter } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Skeleton,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import { usePolicies, type Policy } from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import { policyColumns, isAwsManaged } from './policy-columns';

// The only meaningful filter dimension for a policy list — AWS-managed built-ins
// vs customer-managed policies (derived from the ARN account segment).
const filterFields: FilterField[] = [
  {
    value: 'type',
    label: 'Type',
    input: 'multiselect',
    operators: [{ value: 'is_any_of', label: 'is any of' }],
    options: [
      { value: 'aws', label: 'AWS managed' },
      { value: 'customer', label: 'Customer managed' },
    ],
  },
];

/** IAM managed policies — list and create; click a row to view (and, for
 * customer-managed policies, delete) on the detail page. */
export function PolicyList({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { data = [], isLoading } = usePolicies(serviceId);

  const [search, setSearch] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const base = `/services/oss/${serviceId}`;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Type filter values are collected across the multiselect condition(s).
    const types = new Set(
      conditions
        .filter((c) => c.field === 'type')
        .flatMap((c) => (c.value || '').split(','))
        .map((s) => s.trim())
        .filter(Boolean),
    );
    return data.filter((p) => {
      if (q && !p.policy_name.toLowerCase().includes(q)) return false;
      if (types.size && !types.has(isAwsManaged(p.arn) ? 'aws' : 'customer')) return false;
      return true;
    });
  }, [data, search, conditions]);

  // arn is opaque with reserved chars — encode it into the detail route.
  const onView = (p: Policy) => navigate(`${base}/policies/${encodeURIComponent(p.arn)}`);

  const columns = policyColumns();

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<S3ConsoleBreadcrumbs trail={[{ label: 'Policies' }]} />}
        title={`Policies${data.length ? ` (${data.length})` : ''}`}
        description="IAM managed policies for this storage service."
        actions={
          <Button
            variant="primary"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`${base}/policies/create`)}
          >
            Create policy
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search policies…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{conditions.length ? ` (${conditions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    setConditions(c.filter((x) => x.value.trim()));
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {conditions.length > 0 && (
              <Button variant="ghost" onClick={() => setConditions([])}>
                Clear
              </Button>
            )}
          </>
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={44} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.arn}
          onRowClick={onView}
          emptyState={
            data.length === 0 ? (
              <EmptyState
                icon={<ScrollText size={20} />}
                title="No policies yet"
                description="Create a managed policy to grant scoped access to buckets and objects."
                actions={
                  <Button
                    size="sm"
                    startIcon={<Plus size={14} />}
                    onClick={() => navigate(`${base}/policies/create`)}
                  >
                    Create policy
                  </Button>
                }
              />
            ) : (
              'No policies match your search or filters.'
            )
          }
          footer={data.length ? `Showing ${rows.length} of ${data.length}` : undefined}
        />
      )}
    </Container>
  );
}
